import React, { useState } from 'react';
import {
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Switch,
  FormControlLabel,
  Chip,
  Typography,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
} from '@mui/material';
import { Add, Edit, Delete, TrendingUp, CheckCircle } from '@mui/icons-material';
import axios from 'axios';
import { API_BASE_URL } from '../../../config/api';

const ProfitMarginsTab = ({ data, onRefresh, onSuccess, onError }) => {
  const [openDialog, setOpenDialog] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({
    marginName: '',
    marginPercentage: 0,
    appliesTo: 'All',
    isActive: true,
    isDefault: false,
  });

  const appliesToOptions = ['All', 'Transport', 'Accommodation', 'Services'];

  const handleOpenDialog = (item = null) => {
    if (item) {
      setEditingItem(item);
      setFormData({
        marginName: item.marginName,
        marginPercentage: item.marginPercentage,
        appliesTo: item.appliesTo,
        isActive: item.isActive,
        isDefault: item.isDefault || false,
      });
    } else {
      setEditingItem(null);
      setFormData({
        marginName: '',
        marginPercentage: 0,
        appliesTo: 'All',
        isActive: true,
        isDefault: false,
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingItem(null);
  };

  const handleSave = async () => {
    try {
      if (editingItem) {
        await axios.put(
          `${API_BASE_URL}/pricing/profit-margins/${editingItem.id}`,
          formData
        );
        onSuccess('Profit margin updated successfully');
      } else {
        await axios.post(`${API_BASE_URL}/pricing/profit-margins`, formData);
        onSuccess('Profit margin created successfully');
      }
      handleCloseDialog();
      onRefresh();
    } catch (err) {
      onError('Failed to save: ' + err.message);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this profit margin?')) {
      try {
        await axios.delete(`${API_BASE_URL}/pricing/profit-margins/${id}`);
        onSuccess('Profit margin deleted successfully');
        onRefresh();
      } catch (err) {
        onError('Failed to delete: ' + err.message);
      }
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h6">Profit Margins Management</Typography>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => handleOpenDialog()}
        >
          Add Profit Margin
        </Button>
      </Box>

      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Margin Name</TableCell>
              <TableCell>Percentage</TableCell>
              <TableCell>Applies To</TableCell>
              <TableCell>Default</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data.map((item) => (
              <TableRow key={item.id}>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <TrendingUp fontSize="small" color="action" />
                    <Typography fontWeight="medium">{item.marginName}</Typography>
                  </Box>
                </TableCell>
                <TableCell>
                  <Typography fontWeight="bold" color="primary">
                    {item.marginPercentage.toFixed(2)}%
                  </Typography>
                </TableCell>
                <TableCell>
                  <Chip label={item.appliesTo} color="secondary" size="small" variant="outlined" />
                </TableCell>
                <TableCell>
                  {item.isDefault && (
                    <Chip
                      icon={<CheckCircle />}
                      label="Default"
                      color="primary"
                      size="small"
                    />
                  )}
                </TableCell>
                <TableCell>
                  <Chip
                    label={item.isActive ? 'Active' : 'Inactive'}
                    color={item.isActive ? 'success' : 'default'}
                    size="small"
                  />
                </TableCell>
                <TableCell align="right">
                  <IconButton
                    size="small"
                    color="primary"
                    onClick={() => handleOpenDialog(item)}
                  >
                    <Edit />
                  </IconButton>
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => handleDelete(item.id)}
                  >
                    <Delete />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingItem ? 'Edit Profit Margin' : 'Add Profit Margin'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            <TextField
              label="Margin Name"
              value={formData.marginName}
              onChange={(e) =>
                setFormData({ ...formData, marginName: e.target.value })
              }
              fullWidth
              helperText="Descriptive name for this margin (e.g., 'Standard Markup', 'Premium Tier')"
            />
            <TextField
              label="Margin Percentage (%)"
              type="number"
              value={formData.marginPercentage}
              onChange={(e) =>
                setFormData({ ...formData, marginPercentage: parseFloat(e.target.value) })
              }
              inputProps={{ min: 0, max: 100, step: 0.01 }}
              fullWidth
            />
            <FormControl fullWidth>
              <InputLabel>Applies To</InputLabel>
              <Select
                value={formData.appliesTo}
                label="Applies To"
                onChange={(e) =>
                  setFormData({ ...formData, appliesTo: e.target.value })
                }
              >
                {appliesToOptions.map((option) => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.isDefault}
                  onChange={(e) =>
                    setFormData({ ...formData, isDefault: e.target.checked })
                  }
                />
              }
              label="Set as Default"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={formData.isActive}
                  onChange={(e) =>
                    setFormData({ ...formData, isActive: e.target.checked })
                  }
                />
              }
              label="Active"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSave} variant="contained">
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ProfitMarginsTab;
